import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// Notification times are Istanbul time wherever the browser is, as in the e-mails.
export const NOTIFICATION_TZ = "Europe/Istanbul";

export const formatDateTime = (value) => (value ? dayjs(value).tz(NOTIFICATION_TZ).format("DD.MM.YYYY HH:mm") : "-");
export const formatDay = (day) => (day ? day.split("-").reverse().join(".") : "-");

// <input type="datetime-local"> values are read and written as Istanbul time.
export const toInputValue = (value) => (value ? dayjs(value).tz(NOTIFICATION_TZ).format("YYYY-MM-DDTHH:mm") : "");
export const fromInputValue = (value) => (value ? dayjs.tz(value, NOTIFICATION_TZ).toISOString() : null);

// Shared colors of the notifications panel.
export const UI = {
    text: "#0f172a",
    muted: "#64748b",
    border: "#e2e8f0",
    subtle: "#f8fafc",
    tone: {
        neutral: { fg: "#475569", bg: "#f1f5f9", border: "#cbd5e1" },
        info: { fg: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
        success: { fg: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
        warning: { fg: "#b45309", bg: "#fffbeb", border: "#fde68a" },
        danger: { fg: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
    },
};

export const TYPE_DESCRIPTIONS = {
    ETA_POD: "Varış limanı ETA'sı; istenirse ETA'dan önce hatırlatma",
    ETA_TRANSIT: "Her aktarma limanı için ayrı ETA",
    COB: "Yalnızca doğrulanmış yükleme verisinden; booking onayı sayılmaz",
    ETA_CHANGE: "Eski ve yeni ETA, fark ve neden",
    ARRIVE_TRANSIT: "Aktarma limanına gerçek varış",
    LOAD_CONNECTING: "Konteynerin bağlantı gemisine yüklenmesi",
    DEPART_CONNECTING: "Bağlantı gemisinin aktarma limanından hareketi",
    ARRIVE_POD: "Nihai tahliye limanına gerçek varış",
    DISCHARGE: "Konteynerin gemiden tahliyesi",
    GATE_OUT: "Terminalden çıkış; çekim uyarılarını durdurur",
    EMPTY_RETURN: "Boş konteynerin iadesi; detention takibini kapatır",
    OVERDUE_ARRIVAL: "ETA geçti ama gerçek varış bilgisi gelmedi",
    NOT_PICKED_UP: "Tahliye edildi, terminalden henüz çıkmadı",
    FREE_TIME_WARNING: "Son ücretsiz güne kalan gün sayısı",
    FREE_TIME_EXPIRED: "Serbest süre aşıldı; ücret işlemeye başladı",
    MISSING_INFO: "Serbest süre hesaplanamıyor; eksik bilgi var",
};

// Air and road files: where a type reads differently from ocean.
const MODE_TYPE_DESCRIPTIONS = {
    AIR: {
        ETA_POD: "Varış havalimanı ETA'sı (AMBL / dosya); istenirse ETA'dan önce hatırlatma",
        DEPARTED: "AMBL'deki COB ya da dosyadaki gerçek kalkış (ATD)",
        ETA_CHANGE: "Eski ve yeni ETA, fark ve AMBL'deki gecikme nedeni",
        ARRIVE_POD: "AMBL'deki gerçek varış (AAD)",
        DELIVERED: "Dosyadaki gerçek teslim (ADD)",
        POD_RECEIVED: "Teslim belgesi (POD) tarihi girildiğinde",
        OVERDUE_DEPARTURE: "ETD geçti ama kalkış bilgisi girilmedi",
        OVERDUE_ARRIVAL: "ETA geçti ama varış / teslim bilgisi girilmedi",
        LATE_DELIVERY: "İstenen teslim tarihi (RDL) geçti, teslim yok",
        POD_MISSING: "Teslim edildi, POD henüz girilmedi",
    },
    ROAD: {
        ETA_POD: "Teslim noktası ETA'sı (RMBL / dosya); istenirse ETA'dan önce hatırlatma",
        DEPARTED: "RMBL'deki COB ya da dosyadaki gerçek hareket (ATD)",
        ETA_CHANGE: "Eski ve yeni ETA ve fark",
        DELIVERED: "Dosyadaki gerçek teslim (ADD)",
        POD_RECEIVED: "Teslim belgesi (POD) tarihi girildiğinde",
        OVERDUE_DEPARTURE: "ETD geçti ama araç çıkış bilgisi girilmedi",
        OVERDUE_ARRIVAL: "ETA geçti ama teslim bilgisi girilmedi",
        LATE_DELIVERY: "İstenen teslim tarihi (RDL) geçti, teslim yok",
        POD_MISSING: "Teslim edildi, POD henüz girilmedi",
    },
};

export const typeDescription = (type, mode = "OCEAN") => MODE_TYPE_DESCRIPTIONS[mode]?.[type] || TYPE_DESCRIPTIONS[type] || "";

export const MILESTONE_LABELS = {
    COB: "COB",
    ARRIVE_POD: "POD varış",
    DISCHARGE: "Tahliye",
    AVAILABLE: "Çekime hazır",
    GATE_OUT: "Terminal çıkışı",
    EMPTY_RETURN: "Boş iade",
};

export const CLOCK_LABELS = {
    DEMURRAGE: "Demurrage",
    DETENTION: "Detention",
    STORAGE: "Terminal ardiye",
    COMBINED: "Birleşik (dem + det)",
};

export const STATUS_LABELS = {
    PENDING_APPROVAL: { label: "Onay bekliyor", color: "warning" },
    SENT: { label: "Gönderildi", color: "success" },
    PARTIAL: { label: "Kısmen gönderildi", color: "warning" },
    FAILED: { label: "Hata", color: "error" },
    CANCELLED: { label: "İptal", color: "default" },
    SUPERSEDED: { label: "Güncellendi", color: "default" },
    REJECTED: { label: "Reddedildi", color: "default" },
};

export const SOURCE_OPTIONS = [
    { value: "MANUAL", label: "Operatör" },
    { value: "TERMINAL", label: "Terminal" },
    { value: "CARRIER", label: "Taşıyıcı" },
    { value: "CARRIER_EMAIL", label: "Taşıyıcı e-postası" },
];

// One line describing a free time clock as the server evaluated it.
export const clockSummary = (clock) => {
    const label = CLOCK_LABELS[clock.type] || clock.type;
    switch (clock.status) {
        case "NOT_STARTED":
            return { label: `${label}: başlamadı`, color: "default" };
        case "MISSING_INFO":
            return { label: `${label}: bilgi eksik (${clock.missing.join(", ")})`, color: "warning" };
        case "RUNNING":
            return {
                label: `${label}: ${clock.daysLeft} gün kaldı · son ücretsiz gün ${formatDay(clock.lastFreeDay)} ${clock.lastFreeTime}`,
                color: clock.daysLeft <= 1 ? "error" : clock.daysLeft <= 3 ? "warning" : "success",
            };
        case "EXPIRED":
            return { label: `${label}: ${clock.overdueDays} gün aşıldı · son ücretsiz gün ${formatDay(clock.lastFreeDay)}`, color: "error" };
        case "STOPPED":
            return {
                label: `${label}: durdu${clock.overdueDays ? ` (${clock.overdueDays} gün aşımla)` : ""}`,
                color: clock.overdueDays ? "error" : "default",
            };
        default:
            return { label, color: "default" };
    }
};

export const alertText = (alert) => {
    const d = alert.data || {};
    if (d.clock?.lastFreeDay) {
        const c = clockSummary(d.clock);
        return c.label;
    }
    if (alert.type === "OVERDUE_ARRIVAL") {
        const where = d.port || d.destination;
        return `${where ? `${where}: ` : ""}ETA ${formatDateTime(d.eta)} geçti; güncel varış durumunu kontrol ediniz.`;
    }
    if (alert.type === "NOT_PICKED_UP") return `Tahliye ${formatDateTime(d.dischargedAt)}; konteyner henüz limandan çekilmedi.`;
    if (alert.type === "MISSING_INFO") return `Eksik: ${(d.missing || []).join(", ")}`;
    if (alert.type === "OVERDUE_DEPARTURE") return `ETD ${formatDateTime(d.etd)} geçti; hareket bilgisini (ATD) dosyaya giriniz.`;
    if (alert.type === "LATE_DELIVERY") return `İstenen teslim tarihi ${formatDateTime(d.rdl)} geçti; teslim bilgisi yok.`;
    if (alert.type === "POD_MISSING") return `Teslim ${formatDateTime(d.deliveredAt)}; teslim belgesi (POD) henüz girilmedi.`;
    return "";
};
